import { CaretDownIcon } from '@persona-id/design-tokens/icons';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import RichTextEditor from 'dashboard/components-lib/RichTextEditor';
import MentionExtension from 'dashboard/components-lib/RichTextEditor/Mentions/MentionsExtension';
import { CaseCommentTemplate } from 'dashboard/models/case-template';
import { User } from 'dashboard/models/user';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { SessionStorageKey } from 'lib/clients/StorageClient';
import { useSessionStorageMap } from 'lib/hooks/useSessionStorageMap';
import { useFormSecurely } from 'lib/security/reactHookForm';

const StyledDropdownMenu = styled(DropdownMenu)`
  width: var(--radix-dropdown-menu-trigger-width);
`;

const StyledDropdownItem = styled(DropdownMenu.Item)`
  width: 100%;
  height: 30px;
  font-size: small;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ButtonContainer = styled('div')`
  justify-self: flex-end;
`;
export interface FormAddCaseCommentProps {
  scope: string;
  disableForm?: boolean;
  caseCommentTemplates: CaseCommentTemplate[];
  addComment: (data: AddCaseCommentFormData) => void;
  users: User[];
}

const FormAddCaseComment = ({
  scope,
  caseCommentTemplates,
  addComment,
  disableForm = false,
  users,
}: FormAddCaseCommentProps) => {
  const [commentDraft, setCommentDraft] = useSessionStorageMap<AddCaseCommentFormData>(
    SessionStorageKey.CommentModuleDrafts,
    scope,
    { message: '' },
  );

  const {
    setValue,
    handleSubmit,
    reset,
    control,
    formState: { isDirty, isSubmitting, isValid },
  } = useFormSecurely<AddCaseCommentFormData>({
    mode: 'onChange',
    defaultValues: { message: '' },
  });
  const [editorKey, setEditorKey] = useState(uuid());

  useEffect(() => {
    // set dirty and validate draft as if it was user input so submit button can be enabled.
    setValue('message', commentDraft.message, { shouldDirty: true, shouldValidate: true });
    setEditorKey(uuid());
  }, []);

  const caseCommentItems = caseCommentTemplates.map((template) => {
    return {
      id: template.token,
      label: template.name,
      onClick: () => {
        setValue('message', template.body, { shouldDirty: false, shouldValidate: false });
        setCommentDraft({ message: template.body });
        setEditorKey(uuid());
      },
    };
  });

  const handleAddComment = async (data: AddCaseCommentFormData) => {
    await addComment(data);
    handleReset();
  };

  const handleReset = () => {
    reset();
    setCommentDraft({ message: '' });
    // Resetting the key on a component resets the component
    setEditorKey(uuid());
  };

  return (
    <form onSubmit={handleSubmit(handleAddComment)}>
      <Controller
        name='message'
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange } }) => {
          const handleOnChange = (value: string) => {
            onChange(value);
            setCommentDraft({ message: value });
          };

          return (
            <RichTextEditor
              key={editorKey}
              defaultValue={value}
              onChange={handleOnChange}
              additionalEditorExtensions={[MentionExtension({ users })]}
              onKeyDown={(evt) => {
                // submits form on command + enter
                if (evt?.key === 'Enter' && evt.metaKey && isDirty && isValid) {
                  handleSubmit(handleAddComment)();
                }
              }}
            />
          );
        }}
      />
      <Spacer height={1} />
      <HStack equal={true}>
        {caseCommentTemplates.length > 0 && (
          <StyledDropdownMenu
            trigger={
              <Button
                id='form-add-case-comment__preset'
                tabIndex={0}
                variant='basic'
                width='auto'
                suffixIcon={<CaretDownIcon />}
                disabled={disableForm}
                aria-label='Preset comment'
              >
                Preset comment
              </Button>
            }
            align='start'
          >
            {caseCommentItems.map((item) => (
              <StyledDropdownItem id={item.id} onClick={item.onClick} key={item.id}>
                {item.label}
              </StyledDropdownItem>
            ))}
          </StyledDropdownMenu>
        )}
        <ButtonContainer>
          <Button
            type='submit'
            disabled={isSubmitting || !isDirty || !isValid || disableForm}
            data-test='form-add-case-comment__comment'
            isPending={isSubmitting}
            width='auto'
            variant='basic'
          >
            Comment
          </Button>
        </ButtonContainer>
      </HStack>
    </form>
  );
};

export interface AddCaseCommentFormData {
  message: string;
}

export default FormAddCaseComment;
